/**
 * Attendance Service
 * Comprehensive attendance tracking API service
 */

import api from '../lib/api';

export const attendanceService = {
  // ============================================================================
  // CHECK-IN / CHECK-OUT
  // ============================================================================

  /**
   * Check in a student
   * @param {Object} data - { studentId, classId, checkInTime?, method?, notes? }
   */
  async checkIn(data) {
    const response = await api.post('/attendance/check-in', data);
    return response.data;
  },

  /**
   * Check out a student
   * @param {Object} data - { studentId, classId, checkOutTime?, notes? }
   */
  async checkOut(data) {
    const response = await api.post('/attendance/check-out', data);
    return response.data;
  },

  /**
   * Bulk check-in for multiple students
   * @param {Object} data - { classId, studentIds[], checkInTime?, method?, notes? }
   */
  async bulkCheckIn(data) {
    const response = await api.post('/attendance/bulk-check-in', data);
    return response.data;
  },

  /**
   * Bulk check-out for multiple students
   * @param {Object} data - { classId, studentIds[], checkOutTime?, notes? }
   */
  async bulkCheckOut(data) {
    const response = await api.post('/attendance/bulk-check-out', data);
    return response.data;
  },

  // ============================================================================
  // DAILY OPERATIONS
  // ============================================================================

  /**
   * Get today's attendance for a class
   * @param {string} classId
   */
  async getTodayByClass(classId) {
    const response = await api.get(`/attendance/today/${classId}`);
    return response.data;
  },

  /**
   * Mark a student as absent
   * @param {Object} data - { studentId, classId, date?, reason?, notes? }
   */
  async markAbsent(data) {
    const response = await api.post('/attendance/mark-absent', data);
    return response.data;
  },

  /**
   * Mark a student as present
   * @param {Object} data - { studentId, classId, date?, notes? }
   */
  async markPresent(data) {
    const response = await api.post('/attendance/mark-present', data);
    return response.data;
  },

  /**
   * Update an attendance record
   * @param {string} id - Attendance record ID
   * @param {Object} data - { status?, checkInTime?, checkOutTime?, notes? }
   */
  async update(id, data) {
    const response = await api.put(`/attendance/${id}`, data);
    return response.data;
  },

  // ============================================================================
  // QUERIES
  // ============================================================================

  /**
   * List attendance records with filters
   * @param {Object} params - { studentId?, classId?, date?, startDate?, endDate?, status? }
   */
  async getAll(params = {}) {
    const response = await api.get('/attendance', { params });
    return response.data;
  },

  /**
   * Get attendance history for a specific student
   * @param {string} studentId
   * @param {Object} params - { startDate?, endDate?, classId?, limit? }
   */
  async getStudentHistory(studentId, params = {}) {
    const response = await api.get(`/attendance/student/${studentId}/history`, { params });
    return response.data;
  },

  /**
   * Get currently checked-in students
   * @param {Object} params - { classId? }
   */
  async getCurrentlyCheckedIn(params = {}) {
    const response = await api.get('/attendance/currently-checked-in', { params });
    return response.data;
  },

  // ============================================================================
  // REPORTS
  // ============================================================================

  /**
   * Get daily attendance report
   * @param {Object} params - { date?, classId? }
   */
  async getDailyReport(params = {}) {
    const response = await api.get('/attendance/reports/daily', { params });
    return response.data;
  },

  /**
   * Get weekly attendance report
   * @param {Object} params - { weekStart?, classId? }
   */
  async getWeeklyReport(params = {}) {
    const response = await api.get('/attendance/reports/weekly', { params });
    return response.data;
  },

  /**
   * Get monthly attendance report
   * @param {Object} params - { month?, year?, classId? }
   */
  async getMonthlyReport(params = {}) {
    const response = await api.get('/attendance/reports/monthly', { params });
    return response.data;
  },

  /**
   * Get late arrivals report
   * @param {Object} params - { date?, classId?, scheduledTime? }
   */
  async getLateArrivals(params = {}) {
    const response = await api.get('/attendance/reports/late-arrivals', { params });
    return response.data;
  },

  /**
   * Get early departures report
   * @param {Object} params - { date?, classId?, scheduledEndTime? }
   */
  async getEarlyDepartures(params = {}) {
    const response = await api.get('/attendance/reports/early-departures', { params });
    return response.data;
  },

  /**
   * Get attendance summary statistics
   * @param {Object} params - { startDate, endDate, classId? }
   */
  async getSummary(params = {}) {
    const response = await api.get('/attendance/summary/report', { params });
    return response.data;
  },

  // ============================================================================
  // REAL-TIME / DASHBOARD
  // ============================================================================

  /**
   * Get real-time dashboard data
   */
  async getRealtimeDashboard() {
    const response = await api.get('/attendance/realtime/dashboard');
    return response.data;
  },

  /**
   * Get Supabase realtime subscription configuration
   */
  async getRealtimeConfig() {
    const response = await api.get('/attendance/realtime/subscribe-config');
    return response.data;
  },

  // ============================================================================
  // AUDIT
  // ============================================================================

  /**
   * Get audit log for an attendance record
   * @param {string} id - Attendance record ID
   */
  async getAuditLog(id) {
    const response = await api.get(`/attendance/${id}/audit-log`);
    return response.data;
  }
};
